import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, LogOut } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { collection, query, getDocs, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { signOut } from '../auth';
import { addExerciseToSession } from '../services/overrideService';
import { excludeExercise, unexcludeExercise } from '../services/exclusionService';
import exercisesData from '../data/exercises.json';
import toast from 'react-hot-toast';

const MUSCLE_GROUPS = ['chest', 'back', 'shoulders', 'legs', 'core', 'biceps', 'triceps'];
const TIERS = [
  { value: 0, label: 'No equipment' },
  { value: 1, label: 'Hotel gym' },
  { value: 2, label: 'Standard gym' },
  { value: 3, label: 'Well equipped' },
];

function ExerciseLibraryCard({ exercise, workoutState, currentTier, onAddExercise, isExcluded, onExclude, onUnexclude }) {
  const [expanded, setExpanded] = useState(false);

  const tierMismatch = exercise.minTier > currentTier;
  const substitute = tierMismatch
    ? exercisesData.find(
        (e) =>
          exercise.substitutes.includes(e.id) && e.minTier <= currentTier
      )
    : null;

  const tierLabel = (tier) => TIERS.find((t) => t.value === tier)?.label || 'Unknown';

  const handlePrimaryAction = () => {
    if (workoutState === 'in-progress') {
      // Do it next
      onAddExercise(exercise.id, workoutState, 'next');
    } else if (workoutState === 'planned') {
      // Add to today
      onAddExercise(exercise.id, workoutState, 'append');
    } else {
      // Do it now
      onAddExercise(exercise.id, workoutState, 'now');
    }
  };

  const handleSecondaryAction = () => {
    if (workoutState === 'in-progress') {
      // Add to end
      onAddExercise(exercise.id, workoutState, 'append');
    } else if (workoutState === 'planned') {
      // Start workout with this first
      onAddExercise(exercise.id, workoutState, 'first');
    } else {
      // Add to today
      onAddExercise(exercise.id, workoutState, 'append');
    }
  };

  const handleSubstituteAction = () => {
    if (substitute) {
      onAddExercise(substitute.id, workoutState, workoutState === 'in-progress' ? 'next' : 'append');
    }
  };

  const getPrimaryLabel = () => {
    if (workoutState === 'in-progress') return 'Do it next';
    if (workoutState === 'planned') return 'Add to today';
    return 'Do it now';
  };

  const getSecondaryLabel = () => {
    if (workoutState === 'in-progress') return 'Add to end';
    if (workoutState === 'planned') return 'Start workout with this first';
    return 'Add to today';
  };

  return (
    <div className="border border-light-border dark:border-dark-border rounded-[16px] overflow-hidden bg-light-surface dark:bg-dark-surface">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 text-left hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-plex font-semibold text-[15px] text-light-ink dark:text-dark-ink">
              {exercise.name}
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="px-2 py-1 bg-light-accent-soft dark:bg-dark-accent-soft text-light-ink dark:text-dark-ink text-[12px] rounded-[8px] capitalize font-plex">
                {exercise.muscleGroup}
              </span>
              <span className="px-2 py-1 bg-light-border dark:bg-dark-border text-light-muted dark:text-dark-muted text-[12px] rounded-[8px] capitalize font-plex">
                {exercise.movementPattern}
              </span>
              <span className="px-2 py-1 bg-light-border dark:bg-dark-border text-light-muted dark:text-dark-muted text-[12px] rounded-[8px] font-plex">
                Tier {exercise.minTier}+
              </span>
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-light-border dark:border-dark-border p-4 bg-light-bg dark:bg-dark-bg space-y-4">
          {/* Tier Mismatch Warning */}
          {tierMismatch && (
            <div className="p-3 bg-light-accent-soft dark:bg-dark-accent-soft rounded-[12px]">
              <p className="text-[13px] font-plex text-light-ink dark:text-dark-ink">
                Needs {tierLabel(exercise.minTier).toLowerCase()}, you are at {tierLabel(currentTier).toLowerCase()}
              </p>
              {substitute ? (
                <button
                  onClick={handleSubstituteAction}
                  className="mt-2 text-[13px] font-plex font-semibold text-light-accent dark:text-dark-accent underline"
                >
                  Try {substitute.name} instead
                </button>
              ) : (
                <p className="mt-2 text-[13px] font-plex text-light-muted dark:text-dark-muted">
                  No substitute available at your tier
                </p>
              )}
            </div>
          )}

          {/* Images */}
          {exercise.images.length > 0 && (
            <div className="flex gap-2 overflow-x-auto">
              {exercise.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${exercise.name} ${i + 1}`}
                  className="h-32 rounded-[14px] object-cover"
                />
              ))}
            </div>
          )}

          {/* Description */}
          {exercise.description && (
            <div className="text-[13px] font-plex text-light-ink dark:text-dark-ink">
              <p className="font-semibold mb-1">Description</p>
              <p className="text-light-muted dark:text-dark-muted">{exercise.description}</p>
            </div>
          )}

          {/* Details */}
          <div className="text-[13px] font-plex text-light-ink dark:text-dark-ink">
            <p className="font-semibold mb-1">Details</p>
            <ul className="space-y-1 text-light-muted dark:text-dark-muted">
              <li>
                <span className="font-medium text-light-ink dark:text-dark-ink">Mechanic:</span> {exercise.mechanic}
              </li>
              <li>
                <span className="font-medium text-light-ink dark:text-dark-ink">Equipment:</span>{' '}
                {exercise.equipment.join(', ')}
              </li>
              <li>
                <span className="font-medium text-light-ink dark:text-dark-ink">Primary:</span>{' '}
                {exercise.primaryMuscles.join(', ')}
              </li>
              {exercise.secondaryMuscles.length > 0 && (
                <li>
                  <span className="font-medium text-light-ink dark:text-dark-ink">Secondary:</span>{' '}
                  {exercise.secondaryMuscles.join(', ')}
                </li>
              )}
              <li>
                <span className="font-medium text-light-ink dark:text-dark-ink">Default:</span>{' '}
                {exercise.defaultSets} × {exercise.defaultReps}
              </li>
            </ul>
          </div>

          {/* Cues */}
          {exercise.cues && exercise.cues.length > 0 && (
            <div className="text-[13px] font-plex text-light-ink dark:text-dark-ink">
              <p className="font-semibold mb-1">Cues</p>
              <ul className="list-disc list-inside space-y-1 text-light-muted dark:text-dark-muted">
                {exercise.cues.slice(0, 3).map((cue, i) => (
                  <li key={i}>{cue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-2">
            {!isExcluded ? (
              <>
                <button
                  onClick={handlePrimaryAction}
                  className="w-full h-[52px] bg-light-accent dark:bg-dark-accent text-white rounded-[14px] font-plex font-semibold text-[15px] flex items-center justify-center gap-2"
                >
                  <Plus size={18} />
                  {getPrimaryLabel()}
                </button>

                <button
                  onClick={handleSecondaryAction}
                  className="w-full h-[52px] border border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink rounded-[14px] font-plex font-medium text-[15px]"
                >
                  {getSecondaryLabel()}
                </button>

                <button
                  onClick={() => onExclude(exercise.id, exercise.name)}
                  className="w-full h-[44px] text-light-muted dark:text-dark-muted rounded-[14px] font-plex font-medium text-[13px] hover:text-light-ink dark:hover:text-dark-ink transition-colors"
                >
                  Never suggest this again
                </button>
              </>
            ) : (
              <button
                onClick={() => onUnexclude(exercise.id)}
                className="w-full h-[52px] bg-light-accent dark:bg-dark-accent text-white rounded-[14px] font-plex font-semibold text-[15px]"
              >
                Restore exercise
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Library() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [workoutState, setWorkoutState] = useState('none'); // 'in-progress', 'planned', 'none'
  const [currentTier, setCurrentTier] = useState(2);
  const [currentFocus] = useState('');
  const [currentExerciseIndex] = useState(0);
  const [excludedIds, setExcludedIds] = useState([]);
  const [showExcluded, setShowExcluded] = useState(false);

  useEffect(() => {
    const detectWorkoutState = async () => {
      if (!user) return;

      try {
        const today = new Date().toISOString().split('T')[0];

        // Check for in-progress workout (has sessionOverrides for today with started flag)
        // For now, we'll consider any workout navigated to as in-progress
        // This is a simplification - we could add a "startedAt" field to track actual progress

        // Check for planned workout (user has visited /week today)
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const prefs = userDoc.data().prefs || {};
          setCurrentTier(prefs.defaultTier ?? 2);
          setExcludedIds(prefs.excludedExerciseIds || []);

          // Check if there's a workout today by looking at recent workouts
          const workoutsRef = collection(db, 'users', user.uid, 'workouts');
          const q = query(workoutsRef, orderBy('completedAt', 'desc'), limit(1));
          const workoutsSnap = await getDocs(q);

          if (!workoutsSnap.empty) {
            const lastWorkout = workoutsSnap.docs[0].data();
            const lastWorkoutDate = lastWorkout.completedAt?.toDate?.();

            if (lastWorkoutDate) {
              const lastWorkoutDay = lastWorkoutDate.toISOString().split('T')[0];
              if (lastWorkoutDay === today) {
                // Workout already completed today
                setWorkoutState('none');
              } else {
                // Check if user has a plan for today
                setWorkoutState('planned');
              }
            } else {
              setWorkoutState('planned');
            }
          } else {
            setWorkoutState('planned');
          }
        }
      } catch (error) {
        console.error('Error detecting workout state:', error);
        setWorkoutState('none');
      }
    };

    detectWorkoutState();
  }, [user]);

  const filteredExercises = useMemo(() => {
    return exercisesData.filter((exercise) => {
      const matchesSearch =
        search === '' ||
        exercise.name.toLowerCase().includes(search.toLowerCase()) ||
        exercise.primaryMuscles.some((m) => m.toLowerCase().includes(search.toLowerCase()));

      const matchesMuscle = selectedMuscle === '' || exercise.muscleGroup === selectedMuscle;

      const matchesTier = selectedTier === '' || exercise.minTier <= parseInt(selectedTier);

      const notExcluded = showExcluded || !excludedIds.includes(exercise.id);

      return matchesSearch && matchesMuscle && matchesTier && notExcluded;
    });
  }, [search, selectedMuscle, selectedTier, excludedIds, showExcluded]);

  const handleAddExercise = async (exerciseId, state, position) => {
    if (!user) return;

    try {
      const today = new Date().toISOString().split('T')[0];

      if (state === 'none' || position === 'now') {
        // Start an adhoc session with just this exercise
        navigate(`/session/quick?tier=${currentTier}&minutes=30&focus=full-body&exerciseId=${exerciseId}`);
      } else {
        // Add to existing session
        let afterIndex = -1;

        if (position === 'next') {
          afterIndex = currentExerciseIndex;
        } else if (position === 'first') {
          afterIndex = -1;
        } else {
          // append - add at end (we'll use a large number)
          afterIndex = 999;
        }

        await addExerciseToSession(user.uid, today, currentFocus || 'full-body', exerciseId, afterIndex);

        toast.success(
          position === 'next' ? 'Added after current exercise' : position === 'first' ? 'Added at start' : 'Added to session'
        );

        if (position === 'first') {
          navigate('/week');
        }
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      toast.error('Error adding exercise');
    }
  };

  const handleExcludeExercise = async (exerciseId, exerciseName) => {
    if (!user) return;

    try {
      await excludeExercise(user.uid, exerciseId);
      setExcludedIds([...excludedIds, exerciseId]);

      toast(
        (t) => (
          <div className="flex items-center gap-2">
            <span className="font-plex text-[13px]">
              Removed. Gymbo will not suggest this again.
            </span>
            <button
              onClick={async () => {
                await unexcludeExercise(user.uid, exerciseId);
                setExcludedIds(excludedIds.filter((id) => id !== exerciseId));
                toast.dismiss(t.id);
                toast.success('Undone', { duration: 2000 });
              }}
              className="font-plex font-semibold text-[13px] text-light-accent dark:text-dark-accent underline"
            >
              Undo
            </button>
          </div>
        ),
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error excluding exercise:', error);
      toast.error('Error excluding exercise');
    }
  };

  const handleUnexcludeExercise = async (exerciseId) => {
    if (!user) return;

    try {
      await unexcludeExercise(user.uid, exerciseId);
      setExcludedIds(excludedIds.filter((id) => id !== exerciseId));
      toast.success('Exercise restored');
    } catch (error) {
      console.error('Error restoring exercise:', error);
      toast.error('Error restoring exercise');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Error signing out');
    }
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg px-4 py-6">
      <div className="bg-light-surface dark:bg-dark-surface rounded-[18px]">
        <div className="p-4 border-b border-light-border dark:border-dark-border">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-[24px] font-grotesk font-bold text-light-ink dark:text-dark-ink tracking-[-0.015em]">
                Exercise Library
              </h1>
              <p className="mt-1 text-[13px] font-plex text-light-muted dark:text-dark-muted">
                Browse {exercisesData.length} exercises
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 px-3 py-2 rounded-[12px] text-[13px] font-plex font-medium text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
            >
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light-faint dark:text-dark-faint" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-light-border dark:border-dark-border rounded-[12px] bg-light-surface dark:bg-dark-surface text-light-ink dark:text-dark-ink font-plex text-[15px] focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <select
                value={selectedMuscle}
                onChange={(e) => setSelectedMuscle(e.target.value)}
                className="w-full px-3 py-2.5 border border-light-border dark:border-dark-border rounded-[12px] bg-light-surface dark:bg-dark-surface text-light-ink dark:text-dark-ink font-plex text-[15px] focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent"
              >
                <option value="">All muscles</option>
                {MUSCLE_GROUPS.map((muscle) => (
                  <option key={muscle} value={muscle} className="capitalize">
                    {muscle}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="w-full px-3 py-2.5 border border-light-border dark:border-dark-border rounded-[12px] bg-light-surface dark:bg-dark-surface text-light-ink dark:text-dark-ink font-plex text-[15px] focus:outline-none focus:ring-2 focus:ring-light-accent dark:focus:ring-dark-accent"
              >
                <option value="">All equipment</option>
                {TIERS.map((tier) => (
                  <option key={tier.value} value={tier.value}>
                    {tier.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between text-[13px] font-plex">
            {(search || selectedMuscle || selectedTier) && (
              <>
                <span className="text-light-muted dark:text-dark-muted">
                  {filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''} found
                </span>
                <button
                  onClick={() => {
                    setSearch('');
                    setSelectedMuscle('');
                    setSelectedTier('');
                  }}
                  className="text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink"
                >
                  Clear filters
                </button>
              </>
            )}
            {excludedIds.length > 0 && (
              <button
                onClick={() => setShowExcluded(!showExcluded)}
                className="text-light-muted dark:text-dark-muted hover:text-light-ink dark:hover:text-dark-ink"
              >
                {showExcluded ? 'Hide' : 'Show'} excluded ({excludedIds.length})
              </button>
            )}
          </div>
        </div>

        <div className="p-4 pt-0 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
          {filteredExercises.length === 0 ? (
            <div className="text-center py-12">
              <Filter className="w-12 h-12 text-light-faint dark:text-dark-faint mx-auto mb-4" />
              <p className="text-light-muted dark:text-dark-muted font-plex text-[15px]">No exercises found</p>
              <button
                onClick={() => {
                  setSearch('');
                  setSelectedMuscle('');
                  setSelectedTier('');
                }}
                className="mt-4 px-4 py-2.5 bg-light-accent dark:bg-dark-accent text-white rounded-[12px] hover:bg-light-accent-press dark:hover:bg-dark-accent font-plex font-semibold text-[15px]"
              >
                Clear filters
              </button>
            </div>
          ) : (
            filteredExercises.map((exercise) => (
              <ExerciseLibraryCard
                key={exercise.id}
                exercise={exercise}
                workoutState={workoutState}
                currentTier={currentTier}
                onAddExercise={handleAddExercise}
                isExcluded={excludedIds.includes(exercise.id)}
                onExclude={handleExcludeExercise}
                onUnexclude={handleUnexcludeExercise}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Library;
